import React from 'react';
import type { ColourCombination } from '../../../types';
import { MODEL_VIEWER_OPTIONS } from '../../../lib/modelViewerCatalog';
import { supabase } from '../../../lib/supabase';
import type { CanvasSectionBlock } from '../sectionRegistry';
import type { ViewerRandomSelection } from '../modelViewerStage';

export function useModelViewerRandomSelections(blocks: CanvasSectionBlock[]) {
  const needsVisibleColourCombinations = React.useMemo(
    () => blocks.some((block) => block.type === 'model-viewer'),
    [blocks]
  );
  const [visibleColourCombinations, setVisibleColourCombinations] = React.useState<ColourCombination[]>([]);
  const [viewerRandomSelections, setViewerRandomSelections] = React.useState<
    Record<string, ViewerRandomSelection>
  >({});

  React.useEffect(() => {
    if (!needsVisibleColourCombinations) {
      setVisibleColourCombinations((previous) => (previous.length > 0 ? [] : previous));
      return;
    }

    let active = true;

    const loadVisibleColourCombinations = async () => {
      const { data, error } = await supabase
        .from('colour_combinations')
        .select('*')
        .eq('is_visible', true)
        .order('finish_type', { ascending: true })
        .order('name', { ascending: true });

      if (!active) return;

      if (error) {
        if (import.meta.env.DEV) {
          console.warn('[SectionElementLayer] Failed to load visible colour combinations', error);
        }
        setVisibleColourCombinations([]);
        return;
      }

      setVisibleColourCombinations((data ?? []) as ColourCombination[]);
    };

    void loadVisibleColourCombinations();

    return () => {
      active = false;
    };
  }, [needsVisibleColourCombinations]);

  React.useEffect(() => {
    setViewerRandomSelections((previous) => {
      let changed = false;
      const next: Record<string, ViewerRandomSelection> = { ...previous };
      const activeViewerBlockIds = new Set<string>();

      blocks.forEach((block) => {
        if (block.type !== 'model-viewer') return;
        activeViewerBlockIds.add(block.id);
        const content =
          block.content && typeof block.content === 'object'
            ? (block.content as Record<string, unknown>)
            : {};
        const modelSelectionMode = content.modelSelectionMode === 'random' ? 'random' : 'fixed';
        const colourCombinationSelectionMode =
          content.colourCombinationSelectionMode === 'random' ? 'random' : 'fixed';
        const existing = next[block.id] ?? {};
        let selection = existing;
        let blockChanged = false;

        if (modelSelectionMode === 'random') {
          if (!selection.modelPath) {
            const modelOption =
              MODEL_VIEWER_OPTIONS[Math.floor(Math.random() * MODEL_VIEWER_OPTIONS.length)] ?? null;
            if (modelOption) {
              selection = {
                ...selection,
                modelPath: modelOption.value,
                vaseId: modelOption.vaseId,
                posterSrc: modelOption.posterSrc,
                posterAlt: `${modelOption.label} preview`,
              };
              blockChanged = true;
            }
          }
        } else if (
          selection.modelPath ||
          selection.vaseId ||
          selection.posterSrc ||
          selection.posterAlt
        ) {
          const { colourCombinationId } = selection;
          selection = colourCombinationId ? { colourCombinationId } : {};
          blockChanged = true;
        }

        if (colourCombinationSelectionMode === 'random') {
          if (!selection.colourCombinationId && visibleColourCombinations.length > 0) {
            const combination =
              visibleColourCombinations[
                Math.floor(Math.random() * visibleColourCombinations.length)
              ] ?? null;
            if (combination) {
              selection = {
                ...selection,
                colourCombinationId: combination.id,
              };
              blockChanged = true;
            }
          }
        } else if (selection.colourCombinationId) {
          const {
            modelPath,
            vaseId,
            posterSrc,
            posterAlt,
          } = selection;
          selection =
            modelPath || vaseId || posterSrc || posterAlt
              ? { modelPath, vaseId, posterSrc, posterAlt }
              : {};
          blockChanged = true;
        }

        if (blockChanged) {
          if (Object.keys(selection).length > 0) {
            next[block.id] = selection;
          } else {
            delete next[block.id];
          }
          changed = true;
        }
      });

      Object.keys(next).forEach((blockId) => {
        if (!activeViewerBlockIds.has(blockId)) {
          delete next[blockId];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [blocks, visibleColourCombinations]);

  return { visibleColourCombinations, viewerRandomSelections };
}
